/**
 * CihArticleRows — compact article list used in sidecar contexts (CCO detail
 * page, article detail "more" sections). Contained in the standard page
 * container as a single rounded card with internal row dividers. When a
 * challenge label is provided on each row, it renders as a left-side kicker
 * (useful when articles span multiple challenges). Otherwise the layout
 * collapses to a denser two-col.
 *
 * Aliases: article rows, article list, more articles
 */

import { Link } from "@tanstack/react-router";

import { CihSectionHeader } from "~/components/cih/section-header";

export type ArticleRowData = {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly authorName: string;
  readonly challengeTitle?: string | null;
  readonly challengeSlug?: string | null;
};

type Props = {
  readonly articles: ReadonlyArray<ArticleRowData>;
  readonly sectionLabel: string;
  readonly heading: string;
  readonly showChallenge?: boolean;
};

export function CihArticleRows({
  articles,
  sectionLabel,
  heading,
  showChallenge = false,
}: Props) {
  if (articles.length === 0) return null;

  return (
    <>
      <CihSectionHeader label={sectionLabel} title={heading} />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <div className="divide-y divide-gray-200 overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {articles.map((a) => (
            <Link
              key={a.id}
              to="/articles/$slug"
              params={{ slug: a.slug }}
              className={
                showChallenge
                  ? "group grid grid-cols-1 gap-5 px-5 py-7 transition-colors hover:bg-gray-50 md:grid-cols-[180px_1fr_auto] md:gap-10 md:px-8"
                  : "group grid grid-cols-1 items-baseline gap-2 px-5 py-6 transition-colors hover:bg-gray-50 md:grid-cols-[1fr_auto] md:gap-10 md:px-8 md:py-7"
              }
            >
              {showChallenge && a.challengeTitle ? (
                <div className="text-xs font-medium tracking-[0.22em] text-gray-400 uppercase">
                  {a.challengeTitle}
                </div>
              ) : null}
              <div>
                <h3 className="font-heading text-xl font-medium tracking-tight text-gray-900 md:text-2xl">
                  {a.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-gray-600">
                  {a.description}
                </p>
                <div className="mt-3 text-xs text-gray-500">
                  By{" "}
                  <span className="font-medium text-gray-900">
                    {a.authorName}
                  </span>
                </div>
              </div>
              <span className="mt-auto text-sm text-gray-400 group-hover:text-gray-900 md:self-center">
                →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
